import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, X, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYMENTS_ENABLED = process.env.REACT_APP_PAYMENTS_ENABLED === "true";
const PRESETS = [250, 500, 1000, 2500];

export const PayButton = ({ itemName, label = "Pay a deposit", className = "", testid }) => {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);

  if (!PAYMENTS_ENABLED) {
    return (
      <span data-testid={testid ? `${testid}-soon` : "pay-soon"}
        className={`inline-flex items-center gap-2 font-sans text-sm text-brand-sand/45 ${className}`}>
        <CreditCard className="w-4 h-4" /> Online payment coming soon
      </span>
    );
  }

  const pay = async () => {
    const value = parseFloat(amount);
    if (!value || value < 1) {
      toast.error("Enter an amount of at least $1.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        amount: value,
        item_name: itemName,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={testid || "pay-button"}
        className={`group inline-flex items-center gap-2 font-sans text-sm text-brand-aqua hover:text-brand-coral transition-colors ${className}`}>
        <CreditCard className="w-4 h-4" /> {label}
      </button>

      {open && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" data-testid="pay-modal">
          <div className="absolute inset-0 bg-brand-navy/80 backdrop-blur-sm" onClick={() => !loading && setOpen(false)} />
          <div className="relative w-full max-w-md bg-surface border border-brand-teal/30 p-8">
            <button onClick={() => !loading && setOpen(false)} className="absolute top-4 right-4 text-brand-sand/60 hover:text-brand-sand" data-testid="pay-modal-close">
              <X className="w-5 h-5" />
            </button>
            <span className="font-sans text-xs tracking-[0.28em] uppercase text-brand-teal">Secure payment</span>
            <h3 className="mt-3 font-serif text-2xl text-brand-sand">{itemName}</h3>
            <p className="mt-2 font-sans text-sm text-brand-sand/60">Enter the amount from your quote or a deposit to secure your place. Charged in USD.</p>

            <div className="mt-6 flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button key={p} onClick={() => setAmount(String(p))} data-testid={`pay-preset-${p}`}
                  className={`px-4 py-2 border font-sans text-sm transition-colors ${String(p) === amount ? "border-brand-coral text-brand-coral" : "border-brand-teal/30 text-brand-sand/70 hover:border-brand-teal"}`}>
                  ${p}
                </button>
              ))}
            </div>

            <div className="mt-4 flex items-center border border-brand-teal/30 focus-within:border-brand-aqua">
              <span className="pl-4 pr-2 font-serif text-xl text-brand-teal">$</span>
              <input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00" data-testid="pay-amount-input"
                className="w-full bg-transparent py-3 pr-4 font-sans text-brand-sand focus:outline-none placeholder:text-brand-sand/40" />
            </div>

            <button onClick={pay} disabled={loading} data-testid="pay-confirm"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-brand-coral text-brand-navy font-medium hover:bg-brand-sand transition-colors disabled:opacity-60">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <>Continue to payment <CreditCard className="w-4 h-4" /></>}
            </button>
            <p className="mt-3 font-sans text-xs text-brand-sand/40 text-center">Test mode · card 4242 4242 4242 4242</p>
          </div>
        </div>
      )}
    </>
  );
};
